import { useMemo } from "react"

// ** Third Party Components
import { format } from "date-fns"
import {
  classifyStatus,
  resolveEntityStatus,
} from "../analytics-engine"
import type { TaskChartItem, TimeSeriesPeriod, ProjectRow } from "./types"
import { AnalyticsFilters, DictStatus, StatusCategory } from "@pma/types"
import { getChartColor, saturateForChart } from "@pma/utils/helpers"

interface UseTasksComputedParams {
  filteredTasks: any[]
  chartData: TaskChartItem[] | undefined
  timeSeriesRaw: TimeSeriesPeriod[] | undefined
  projectStatsRaw: any
  projectsList: any[]
  statuses: DictStatus[]
  filters?: AnalyticsFilters
  isHebrew: boolean
}

export function useTasksComputed({
  filteredTasks,
  chartData,
  timeSeriesRaw,
  projectStatsRaw,
  projectsList,
  statuses,
  filters,
  isHebrew,
}: UseTasksComputedParams) {
  // ---- KPIs + donut from task data ------------------------------------------
  const { totalTasks, statusData, kpiOpen, kpiCompleted, kpiCanceled } = useMemo(() => {
    if (filteredTasks?.length) {
      const total = filteredTasks.length
      const statusCounts = new Map<
        number,
        { display: { name: string; color: string; category: StatusCategory }; count: number }
      >()

      filteredTasks.forEach((t: any) => {
        const display = resolveEntityStatus(t, statuses)
        const existing = statusCounts.get(display.id)
        if (existing) {
          existing.count++
        } else {
          statusCounts.set(display.id, { display, count: 1 })
        }
      })

      const statusList = Array.from(statusCounts.entries()).map(([id, s]) => ({
        id,
        title: s.display.name,
        count: s.count,
        fill: saturateForChart(s.display.color),
      }))

      let completed = 0,
        canceled = 0
      statusCounts.forEach((s) => {
        if (s.display.category === "completed") completed += s.count
        else if (s.display.category === "canceled") canceled += s.count
      })

      return {
        totalTasks: total,
        statusData: statusList,
        kpiOpen: total - completed - canceled,
        kpiCompleted: completed,
        kpiCanceled: canceled,
      }
    }

    if (chartData?.length) {
      const chartTotal = chartData.reduce((sum, item) => sum + item.count, 0)
      const statusList = chartData.map((item) => {
        const st = item._status
        return {
          id: st?.id || 0,
          title: st?.name || "Unknown",
          count: item.count,
          fill: saturateForChart(st?.color || "#64748b"),
        }
      })
      let completed = 0,
        canceled = 0
      chartData.forEach((item) => {
        const cat = classifyStatus(item._status as any)
        if (cat === "completed") completed += item.count
        else if (cat === "canceled") canceled += item.count
      })
      return {
        totalTasks: chartTotal,
        statusData: statusList,
        kpiOpen: chartTotal - completed - canceled,
        kpiCompleted: completed,
        kpiCanceled: canceled,
      }
    }

    if (timeSeriesRaw?.length) {
      const taskMap = new Map<number, any>()
      timeSeriesRaw.forEach((period) => {
        ;(period?.tasks || []).forEach((t: any) => {
          if (t?.id) taskMap.set(t.id, t)
        })
      })
      const allTasks = Array.from(taskMap.values())
      const total = allTasks.length
      if (total === 0)
        return { totalTasks: 0, statusData: [], kpiOpen: 0, kpiCompleted: 0, kpiCanceled: 0 }

      const statusCounts = new Map<
        number,
        { display: { name: string; color: string; category: StatusCategory }; count: number }
      >()
      allTasks.forEach((t: any) => {
        const display = resolveEntityStatus(t, statuses)
        const existing = statusCounts.get(display.id)
        if (existing) {
          existing.count++
        } else {
          statusCounts.set(display.id, { display, count: 1 })
        }
      })
      const statusList = Array.from(statusCounts.entries()).map(([id, s]) => ({
        id,
        title: s.display.name,
        count: s.count,
        fill: s.display.color,
      }))
      let completed = 0,
        canceled = 0
      statusCounts.forEach((s) => {
        if (s.display.category === "completed") completed += s.count
        else if (s.display.category === "canceled") canceled += s.count
      })
      return {
        totalTasks: total,
        statusData: statusList,
        kpiOpen: total - completed - canceled,
        kpiCompleted: completed,
        kpiCanceled: canceled,
      }
    }

    return {
      totalTasks: 0,
      statusData: [] as { id: number; title: string; count: number; fill: string }[],
      kpiOpen: 0,
      kpiCompleted: 0,
      kpiCanceled: 0,
    }
  }, [filteredTasks, chartData, timeSeriesRaw, statuses])

  // ---- Time series chart data -----------------------------------------------
  const timeSeriesData = useMemo(() => {
    if (!timeSeriesRaw?.length) return []
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - 90)

    let data = timeSeriesRaw
      .map((stat) => ({
        date: format(new Date(stat?.from), "yyyy-MM-dd"),
        created: stat?.created || 0,
        finished: stat?.finished || 0,
      }))
      .filter((item) => new Date(item.date) >= cutoff)

    if (filters?.dateRange?.from) {
      const from = filters.dateRange.from
      data = data.filter((item) => new Date(item.date) >= from)
    }
    if (filters?.dateRange?.to) {
      const to = filters.dateRange.to
      data = data.filter((item) => new Date(item.date) <= to)
    }

    return data
  }, [timeSeriesRaw, filters?.dateRange])

  // ---- Project stats donut --------------------------------------------------
  const projectDonutData = useMemo(() => {
    if (!projectStatsRaw) return []
    const rawStatuses = Array.isArray(projectStatsRaw)
      ? projectStatsRaw
      : Array.isArray(projectStatsRaw?.statuses)
        ? projectStatsRaw.statuses
        : typeof projectStatsRaw?.statuses === "string"
          ? (() => {
              try {
                return JSON.parse(projectStatsRaw.statuses)
              } catch {
                return []
              }
            })()
          : []

    return rawStatuses.map((stat: any, i: number) => {
      const apiColor = stat?._status?.color || stat?.color
      return {
        title: stat?._status?.name || stat?.name || stat?.status || "Unknown",
        count: Number(stat?.count || stat?.total || 0),
        fill: saturateForChart(apiColor || getChartColor(i)),
      }
    })
  }, [projectStatsRaw])

  const totalProjects = useMemo(() => {
    if (projectStatsRaw?.total != null) return Number(projectStatsRaw.total)
    if (projectDonutData.length)
      return projectDonutData.reduce((sum: number, p: { count: number }) => sum + p.count, 0)
    if (projectsList.length) return projectsList.length
    return 0
  }, [projectStatsRaw, projectDonutData, projectsList])

  // ---- Task counts by project -----------------------------------------------
  const taskCountsByProject = useMemo(() => {
    const map = new Map<number, { total: number; done: number }>()
    if (filteredTasks?.length) {
      filteredTasks.forEach((task: any) => {
        const pid = task?.project_id || task?._project?.id
        if (!pid) return
        if (!map.has(pid)) map.set(pid, { total: 0, done: 0 })
        const entry = map.get(pid)!
        entry.total++
        const display = resolveEntityStatus(task, statuses)
        if (display.category === "completed") entry.done++
      })
    }
    return map
  }, [filteredTasks, statuses])

  // ---- Project table data ---------------------------------------------------
  const projectTableData: ProjectRow[] = useMemo(() => {
    if (!projectsList.length) return []
    return projectsList.map((project: any) => {
      const pid = project?.id || 0
      const counts = taskCountsByProject.get(pid)
      const total = counts?.total || 0
      const done = counts?.done || 0
      const completion = total > 0 ? (done / total) * 100 : 0
      const display = resolveEntityStatus(project, statuses)

      return {
        id: pid,
        title: project?.title || "--",
        statusDisplay: { name: display.name, color: display.color },
        totalTasks: total,
        doneTasks: done,
        completion: Math.round(completion * 100) / 100,
      }
    })
  }, [projectsList, taskCountsByProject, statuses])

  // ---- Top Assignees --------------------------------------------------------
  const assigneeData = useMemo(() => {
    const counts: Record<string, number> = {}
    if (filteredTasks?.length) {
      filteredTasks.forEach((task: any) => {
        const users = task?.responsible_id || []
        if (Array.isArray(users)) {
          users.forEach((user: any) => {
            const name = typeof user === "object" ? user?.name || "Unknown" : `User ${user}`
            counts[name] = (counts[name] || 0) + 1
          })
        }
      })
    }
    if (!Object.keys(counts).length) return []
    return Object.entries(counts)
      .map(([name, count], i) => ({
        name,
        count,
        fill: getChartColor(i),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [filteredTasks])

  // ---- Project-level KPIs ---------------------------------------------------
  const activeProjects = useMemo(() => {
    if (projectStatsRaw?.total != null && projectStatsRaw?.finished != null) {
      return Number(projectStatsRaw.total) - Number(projectStatsRaw.finished)
    }
    if (projectDonutData?.length) {
      if (projectsList.length) {
        return projectsList.filter((p: any) => {
          const display = resolveEntityStatus(p, statuses)
          return display.category === "open"
        }).length
      }
      return projectDonutData.reduce((sum: number, p: { count: number }) => sum + p.count, 0)
    }
    return projectsList.length
  }, [projectStatsRaw, projectDonutData, projectsList, statuses])

  const overdueProjects = useMemo(() => {
    if (!projectsList.length) return 0
    const now = new Date()
    return projectsList.filter((p: any) => {
      const deadline = p?.end_date || p?.deadline || p?.finish_to
      if (!deadline) return false
      const display = resolveEntityStatus(p, statuses)
      return new Date(deadline) < now && display.category !== "completed"
    }).length
  }, [projectsList, statuses])

  const atRiskProjects = useMemo(() => {
    if (!projectTableData?.length) return 0
    return projectTableData.filter((p) => p.totalTasks > 0 && p.completion < 30).length
  }, [projectTableData])

  const avgCompletion = useMemo(() => {
    if (!projectTableData?.length) return 0
    const total = projectTableData.reduce((sum, p) => sum + p.completion, 0)
    return Math.round((total / projectTableData.length) * 100) / 100
  }, [projectTableData])

  return {
    totalTasks,
    statusData,
    kpiOpen,
    kpiCompleted,
    kpiCanceled,
    timeSeriesData,
    projectDonutData,
    totalProjects,
    taskCountsByProject,
    projectTableData,
    assigneeData,
    activeProjects,
    overdueProjects,
    atRiskProjects,
    avgCompletion,
  }
}
